import { useGoBack } from 'hooks/useGoBack';
import React, { useContext, useCallback } from 'react';
import { convertUndefinedFields } from 'utils/helpers';
import { IEditSubCategoryContext, EditSubCategoryContext } from '../../editSubCategoryState/editSubCategoryContext';
import { ModalToConfirm } from 'components/ModalWindows/ModalToConfirm';
import ModalSentSuccessfully from 'components/ModalWindows/ModalSentSuccessfully';
import ModalSentError from 'components/ModalWindows/ModalSentError';
import ModalSentErrorWithInfo from './ModalSentErrorWithInfo/ModalSentErrorWithInfo';
import axios from 'axios';
import { ServerErrorTypes } from 'types';
import { AppContext, IAppContext } from 'appState/appContext';
import { updateSubCategory } from 'backend/subcategories';
import classNames from 'classnames';
import style from './ModalWindows.module.scss';

const ModalWindows: React.FC = () => {
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const {
		changeTypeOfModalWindow,
		refetchData,
		showErrorModalWithInfo,
		setFileId,
		dataForSubmit,
		errorResponseData,
		typeOfModalWindow,
		fetchedRestaurants,
		fetchedCategories,
		IDSelectedCategory,
	} = useContext<IEditSubCategoryContext>(EditSubCategoryContext);
	const containerClasses = classNames(style.container, { [style.visible]: typeOfModalWindow || errorResponseData });
	const goBack = useGoBack();

	const handleRedirect = useCallback(() => {
		goBack('/menu/subcategory');
	}, [goBack]);

	const handleConfirm = useCallback(async () => {
		if (dataForSubmit) {
			try {
				changeTypeOfModalWindow(null);
				const convertedData = convertUndefinedFields(dataForSubmit);
				showLoader();
				await updateSubCategory(convertedData);
				changeTypeOfModalWindow('success');
			} catch (err) {
				if (
					axios.isAxiosError(err) &&
					err.response?.data.Type === ServerErrorTypes.SUB_CATEGORY_VALIDATION_FAILED
				) {
					showErrorModalWithInfo(err.response.data);
				} else {
					changeTypeOfModalWindow('error');
				}
			} finally {
				hideLoader();
			}
		}
	}, [dataForSubmit]);

	const handleUpdateData = useCallback(() => {
		refetchData(true);
		changeTypeOfModalWindow(null);
		setFileId(null);
	}, []);

	const handleCloseModal = useCallback(() => {
		changeTypeOfModalWindow(null);
	}, []);

	const handleCloseModalWithInfo = useCallback(() => {
		showErrorModalWithInfo(null);
	}, []);

	return (
		<div className={containerClasses} data-test="ModalWindows">
			<ModalToConfirm
				onConsentEvent={handleRedirect}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmFieldsClearing'}
				description="cancelSaveInfo"
			/>
			<ModalToConfirm
				onConsentEvent={handleConfirm}
				onFailureEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'toConfirmSubmit'}
				description="saveInfo"
			/>
			<ModalSentSuccessfully
				onConsentEvent={handleUpdateData}
				isVisible={typeOfModalWindow === 'success'}
				description="savedSuccessfully"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'error'}
				description="errorWhileSaving"
			/>
			<ModalSentError
				onConsentEvent={handleCloseModal}
				isVisible={typeOfModalWindow === 'defaultTranslationIsEmpty'}
				description="errorDefaultTranslation"
			/>
			<ModalSentErrorWithInfo
				onConsentEvent={handleCloseModalWithInfo}
				isVisible={!!errorResponseData}
				errorResponseData={errorResponseData}
				fetchedRestaurants={fetchedRestaurants}
				fetchedCategories={fetchedCategories}
				IDSelectedCategory={IDSelectedCategory}
			/>
		</div>
	);
};

export default ModalWindows;
